import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Platform } from "react-native";
import { FileStore } from "../api/fileStore.type";
import { uploadFile } from "../api/storage.api";
import {
  AdditionalUploadData,
  AdditionalUploadPurpose,
  MedicalRecordIngestResult,
  UploadAsset,
  UploadMultipleFilesResponse,
} from "../api/storage.type";
import { useToastContext } from "../containers/ToastContextWrapper";

export interface ImageUploadProps {
  userId: string;
  contactId?: string;
  onUploadStart?: (imageUris: string[]) => void;
  onUploadSuccess?: (
    imageUris: string[],
    result?: FileStore[],
    meta?: { medicalRecordIngest?: MedicalRecordIngestResult }
  ) => void;
  onUploadError?: (error: Error) => void;
  purpose?: AdditionalUploadPurpose;
  aspect?: [number, number];
  quality?: number;
  confirmImage?: boolean; // If true, return URI directly instead of processing
  additionalDataProp?: Partial<AdditionalUploadData>;
}

interface UseImageUploadType {
  pickImageFromFiles: () => Promise<string[] | null>;
  openPhotoOptions: () => void;
  isPending: boolean;
  data?: UploadMultipleFilesResponse | null;
  error: Error | null;
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  imageUris: string[] | null;
  selectedAssets: UploadAsset[] | null;
  setAdditionalData: (data: Partial<AdditionalUploadData> | undefined) => void;
  additionalData: Partial<AdditionalUploadData> | undefined;
  triggerUpload: () => void;
}

type UploadPayload = {
  assets: UploadAsset[];
  additionalData: AdditionalUploadData;
};

export const useImageUpload = (
  options: ImageUploadProps
): UseImageUploadType => {
  const { userId, onUploadStart, additionalDataProp } = options;

  const { showToast } = useToastContext();
  const [showDialog, setShowDialog] = useState(false);

  const [imageUris, setImageUris] = useState<string[] | null>(null);
  const [selectedAssets, setSelectedAssets] = useState<UploadAsset[] | null>(
    null
  );
  const [additionalData, setAdditionalDataState] = useState<
    Partial<AdditionalUploadData> | undefined
  >(additionalDataProp);

  const [currentAssetsReadyToUpload, setCurrentAssetsReadyToUpload] = useState<
    UploadAsset[] | null
  >(null);

  // Use React Query's useMutation for the file upload
  const {
    mutate: uploadMutation,
    isPending,
    error,
    data,
  } = useMutation<UploadMultipleFilesResponse, Error, UploadPayload>({
    mutationFn: uploadFile,
    onSuccess: (result: UploadMultipleFilesResponse) => {
      const fileStores = (result?.fileStores || []) as unknown as FileStore[];

      const uris =
        fileStores
          .filter((file) => file.url)
          .map((file) => file.url as string) || [];

      options.onUploadSuccess?.(uris, fileStores, {
        medicalRecordIngest: result?.medicalRecordIngest,
      });

      showToast({
        title: "Success",
        description: "Image uploaded successfully",
        type: "success",
      });
    },
    onError: (error) => {
      const normalizedError =
        error instanceof Error ? error : new Error(String(error));
      options.onUploadError?.(normalizedError);
      showToast({
        title: "Upload failed",
        description:
          normalizedError.message || "Something went wrong while uploading.",
        type: "error",
      });
    },
    onSettled: () => {
      setCurrentAssetsReadyToUpload(null);
      setAdditionalDataState(additionalDataProp);
      setImageUris(null);
      setSelectedAssets(null);
    },
  });

  const setAdditionalData = (
    data: Partial<AdditionalUploadData> | undefined
  ) => {
    setAdditionalDataState(data);
  };

  const isWeb = Platform.OS === "web";

  const buildAdditionalUploadData = (
    overrides?: Partial<AdditionalUploadData>
  ): AdditionalUploadData => {
    const merged: AdditionalUploadData = {
      userId,
      ...(options?.contactId ? { contactId: options.contactId } : {}),
      ...(options.purpose
        ? { purpose: options.purpose }
        : { purpose: "contact_image" }),
      ...(additionalData ?? {}),
      ...(overrides ?? {}),
    };

    if (!merged.subjectUserId && userId) {
      merged.subjectUserId = userId;
    }

    return merged;
  };

  const triggerUpload = () => {
    if (currentAssetsReadyToUpload) {
      uploadMutation({
        assets: currentAssetsReadyToUpload,
        additionalData: buildAdditionalUploadData(),
      });
    } else {
      showToast({
        title: "Error",
        description: "No assets to upload",
      });
    }
  };

  const openPhotoOptions = () => {
    if (isPending) return;
    void pickImageFromFiles();
  };

  const normalizeAsset = async (
    asset: UploadAsset,
    index: number
  ): Promise<UploadAsset> => {
    const isImage =
      (asset.type && asset.type.toLowerCase().startsWith("image")) ||
      (asset.mimeType && asset.mimeType.toLowerCase().startsWith("image"));

    if (!isImage) {
      return asset;
    }

    const fileName = (() => {
      if (asset.fileName?.toLowerCase().endsWith(".jpg")) return asset.fileName;
      if (asset.fileName?.toLowerCase().endsWith(".jpeg"))
        return asset.fileName;
      if (asset.fileName) {
        return `${asset.fileName.replace(/\.[^.]+$/, "")}.jpg`;
      }
      return `image-${Date.now()}-${index}.jpg`;
    })();

    try {
      if (isWeb) {
        if (typeof document === "undefined") {
          return {
            ...asset,
            fileName,
            mimeType: "image/jpeg",
            type: "image",
          };
        }

        const response = await fetch(asset.uri);
        const originalBlob = await response.blob();
        const bitmap = await createImageBitmap(originalBlob);

        const canvas = document.createElement("canvas");
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const context = canvas.getContext("2d");
        if (!context) {
          return {
            ...asset,
            fileName,
            mimeType: "image/jpeg",
            type: "image",
            width: bitmap.width,
            height: bitmap.height,
          };
        }

        context.drawImage(bitmap, 0, 0);

        const compressedBlob: Blob | null = await new Promise((resolve) => {
          canvas.toBlob(
            (blob) => resolve(blob),
            "image/jpeg",
            options.quality ?? 0.85
          );
        });

        if (!compressedBlob) {
          return {
            ...asset,
            fileName,
            mimeType: "image/jpeg",
            type: "image",
            width: bitmap.width,
            height: bitmap.height,
          };
        }

        const file = new File([compressedBlob], fileName, {
          type: "image/jpeg",
        });
        const uri = URL.createObjectURL(file);

        return {
          ...asset,
          uri,
          file,
          fileName,
          mimeType: "image/jpeg",
          type: "image",
          width: bitmap.width,
          height: bitmap.height,
          fileSize: compressedBlob.size,
        } as UploadAsset;
      }

      let manipulatedAsset = asset;

      return {
        ...manipulatedAsset,
        fileName,
        mimeType: "image/jpeg",
        type: "image",
      };
    } catch (error) {
      console.warn("Failed to normalize image asset", error);
      return {
        ...asset,
        fileName,
        mimeType: "image/jpeg",
        type: "image",
      };
    }
  };

  const handleImageSelected = async (assets: UploadAsset[]) => {
    const normalizedAssets = await Promise.all(
      assets.map((asset, index) => normalizeAsset(asset, index))
    );

    setCurrentAssetsReadyToUpload(normalizedAssets);
    setSelectedAssets(normalizedAssets);
    const imageUris = normalizedAssets.map((asset) => asset.uri);

    if (onUploadStart) {
      onUploadStart(imageUris);
    }
    // If onSelection is needed, process image immediately
    setImageUris((prev) => {
      if (prev) {
        return [...prev, ...imageUris];
      }
      return imageUris;
    });

    // If this is for direct use in the component, return the URI
    if (options?.confirmImage) {
      return imageUris;
    } else {
      const uploadData = {
        assets: normalizedAssets,
        additionalData: buildAdditionalUploadData(),
      };
      uploadMutation(uploadData);
    }

    return imageUris;
  };

  const pickImageFromFiles = async (): Promise<string[] | null> => {
    if (typeof document === "undefined") {
      showToast({
        title: "Error",
        description: "File selection is not supported in this environment.",
        type: "error",
      });
      return null;
    }

    return new Promise<string[] | null>((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*,application/pdf";
      input.multiple = true;
      input.style.display = "none";

      const handleChange = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        const files = target.files ? Array.from(target.files) : [];

        input.removeEventListener("change", handleChange);
        document.body.removeChild(input);

        if (files.length === 0) {
          resolve(null);
          return;
        }

        try {
          const uploadAssets = files.map((file, index) => ({
            uri: URL.createObjectURL(file),
            fileName: file.name,
            mimeType: file.type || undefined,
            type: file.type.startsWith("image") ? "image" : undefined,
            width: 0,
            height: 0,
            fileSize: file.size,
            file,
          }));

          const result = await handleImageSelected(uploadAssets);
          resolve(result);
        } catch (error) {
          console.error("Error selecting files:", error);
          showToast({
            title: "Error",
            description: "Failed to select file. Please try again.",
            type: "error",
          });
          resolve(null);
        }
      };

      input.addEventListener("change", handleChange);
      document.body.appendChild(input);
      input.click();
    });
  };

  return {
    openPhotoOptions,

    pickImageFromFiles,
    isPending,
    data,
    error: error as Error | null,
    showDialog,
    setShowDialog,
    imageUris,
    selectedAssets,
    setAdditionalData,
    additionalData,
    triggerUpload,
  };
};
